import { DropdownButtonSize } from '@/storybook/components/FormCollection/components/DropdownButton/DropdownButton';
import { SelectItem } from '@/types/ui';
import { useMemo } from 'react';
import { useMultiSelectHandlers } from './useMultiSelectHandlers';
import { useMultiSelectOptions } from './useMultiSelectOptions';
import { useMultiSelectVirtualization } from './useMultiSelectVirtualization';

type UseMultiSelectParams<T> = {
  options?: SelectItem<T>[];
  unfilteredOptions?: SelectItem<T>[];
  value: T[];
  memoizedValueList: T[];
  searchInput: string | undefined;
  disabled: boolean;
  noCategoryLabel: string;
  onChange: (selectedValue?: T[]) => void;
  customSelectItemFn?: (value: T, options: SelectItem<T>[]) => SelectItem<T> | undefined;
  size?: keyof typeof DropdownButtonSize;
};

export function useMultiSelect<T>({
  options,
  unfilteredOptions,
  value,
  memoizedValueList,
  searchInput,
  disabled,
  noCategoryLabel,
  onChange,
  customSelectItemFn,
  size,
}: UseMultiSelectParams<T>) {
  const fixedOptions = useMemo(() => {
    if (options == null) return [];
    return options.filter(opt => opt.value != null && opt.label != null);
  }, [options]);

  const { handleClick } = useMultiSelectHandlers<T>({ value, disabled, onChange });

  const {
    searchResults,
    isOptionSelected,
    dedupedSelectedOptions,
    notSelectedOptions,
    selectedOptionValues,
    notSelectedOptionValues,
    categoriesToDisplay,
    hasCategorizedOptions,
    needsDivider,
    dropdownButtonValue,
  } = useMultiSelectOptions<T>({
    options,
    unfilteredOptions,
    fixedOptions,
    value,
    memoizedValueList,
    searchInput,
    customSelectItemFn,
  });

  const { virtualizedList, parentRef, renderVirtualRow } = useMultiSelectVirtualization<T>({
    dedupedSelectedOptions,
    notSelectedOptions,
    selectedOptionValues,
    notSelectedOptionValues,
    categoriesToDisplay,
    hasCategorizedOptions,
    needsDivider,
    noCategoryLabel,
    handleClick,
    isOptionSelected,
    size,
  });

  return {
    searchResults,
    dropdownButtonValue,
    virtualizedList,
    parentRef,
    renderVirtualRow,
  };
}
